import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Volume2, VolumeX } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * useAutoNarrate — a reusable hook that gives a page:
 *   - an `autoplay` boolean + `Toggle` component,
 *   - an `audioEl` React node to mount once,
 *   - a `register(id, text)` returning a `ref` callback that watches the element
 *     via IntersectionObserver and auto-plays Ms. Pradhan's TTS the first time
 *     it scrolls into view.
 *
 * Only one section plays at a time (new triggers replace the current audio).
 * Each section is narrated at most once per page-load.  Pausing manually via
 * the audio controls stops the current section but does not disable autoplay.
 */
export function useAutoNarrate({ voice = "coral", threshold = 0.4 } = {}) {
  const { user } = useAuth();
  const [autoplay, setAutoplay] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const audioRef = useRef(null);
  const playedRef = useRef(new Set());

  // Reset played history whenever autoplay is turned on again.
  const toggle = useCallback(() => {
    if (!user) { toast.info("Sign in to hear Ms. Pradhan's voice"); return; }
    setAutoplay((v) => {
      const next = !v;
      if (next) {
        playedRef.current = new Set();
        toast.success("Autoplay on — I'll narrate as you scroll.");
      } else {
        if (audioRef.current) audioRef.current.pause();
        toast("Autoplay off");
      }
      return next;
    });
  }, [user]);

  const play = useCallback(async (id, text) => {
    if (!user || !text) return;
    if (playedRef.current.has(id)) return;
    playedRef.current.add(id);
    setActiveId(id);
    try {
      const token = localStorage.getItem("dpa_token");
      const r = await fetch(`${API}/pradhan/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: text.slice(0, 2500), voice }),
      });
      if (!r.ok) return;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const el = audioRef.current;
      if (!el) return;
      el.src = url;
      await el.play();
    } catch { /* ignore autoplay glitches */ }
  }, [user, voice]);

  const register = useCallback((id, text) => (el) => {
    if (!el || !autoplay) return;
    if (el._io) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting && e.intersectionRatio > threshold) { play(id, text); break; }
      },
      { threshold: [threshold] },
    );
    io.observe(el);
    el._io = io;
  }, [autoplay, play, threshold]);

  const Toggle = useCallback(({ testid = "autoplay-toggle", className = "" }) => (
    <button
      onClick={toggle}
      className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs uppercase tracking-widest font-bold transition ${autoplay ? "border-teal bg-teal text-cream" : "border-ink text-ink hover:bg-ink hover:text-cream"} ${className}`}
      data-testid={testid}
    >
      {autoplay ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
      {autoplay ? "Autoplay on" : "Hands-free narration"}
    </button>
  ), [autoplay, toggle]);

  const AudioEl = useCallback(() => (
    <audio ref={audioRef} preload="none" onEnded={() => setActiveId(null)} className="hidden" data-testid="autonarrate-audio" />
  ), []);

  return { autoplay, activeId, register, Toggle, AudioEl };
}
